import { useState, useEffect, useCallback, useMemo } from "react";
import { useApi } from "../../hooks/useApi";
import { useCompanyId } from "../../hooks/useCompanyId";
import { usePpAccounts } from "../../hooks/usePpAccounts";
import {
  MerchantMappingConfig,
  MerchantMappingSaveBody,
  RemapUnmappedResponse,
  DescriptorMatcher,
  PpType,
} from "../../types/merchantMappingConfig";

interface VerifiNoticeMerchantItem {
  merchant_id: number;
  merchant_name: string | null;
  clean_merchant_name: string | null;
}
import MerchantMappingSettings from "../../components/alerts/MerchantMappingSettings";
import * as Sentry from "@sentry/react";

interface RdrNoticesConfigRaw {
  verifiNoticesAlertsConfigId: number;
  merchantName: string;
  descriptors: string[];
  descriptorMatcher: DescriptorMatcher | null;
  ppType: PpType | null;
  ppAccountId: number | null;
  companyId: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function RdrNoticesSettings() {
  const { get, post, put, del, loading, error: apiError } = useApi();
  const { companyId } = useCompanyId();
  const { ppAccounts } = usePpAccounts();

  const [configs, setConfigs] = useState<RdrNoticesConfigRaw[]>([]);
  const [merchants, setMerchants] = useState<VerifiNoticeMerchantItem[]>([]);

  const fetchConfigs = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await get<RdrNoticesConfigRaw[]>(
        `/api/companies/${companyId}/verifi-notices/merchant-mappings`
      );
      setConfigs(response);
    } catch (err) {
      console.error("Failed to fetch RDR notices configs:", err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  const fetchMerchants = useCallback(async () => {
    if (!companyId) return;
    try {
      const params = `?companyId=${companyId}`;
      const response = await get<{ merchants: VerifiNoticeMerchantItem[] }>(
        `/api/alerts/verifi-notices/merchants${params}`
      );
      setMerchants(response.merchants);
    } catch (err) {
      console.error("Failed to fetch RDR notice merchants:", err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    fetchMerchants();
  }, [fetchMerchants]);

  const merchantNameOptions = useMemo(
    () => [...new Set(merchants.map((m) => m.clean_merchant_name).filter((n): n is string => !!n))].sort(),
    [merchants]
  );

  const sharedConfigs: MerchantMappingConfig[] = useMemo(
    () => configs.map((c) => ({ ...c, configId: c.verifiNoticesAlertsConfigId })),
    [configs]
  );

  const handleCreate = async (body: MerchantMappingSaveBody) => {
    await post(`/api/companies/${companyId}/verifi-notices/merchant-mappings`, body);
  };

  const handleUpdate = async (configId: number, body: MerchantMappingSaveBody) => {
    await put(`/api/companies/${companyId}/verifi-notices/merchant-mappings/${configId}`, body);
  };

  const handleDelete = async (configId: number) => {
    await del(`/api/companies/${companyId}/verifi-notices/merchant-mappings/${configId}`);
  };

  const handleSync = async (): Promise<RemapUnmappedResponse> => {
    return await post<RemapUnmappedResponse>(`/api/alerts/verifi-notices/remap-unmapped`, {}, { slowApi: true });
  };

  const handleSyncCompany = async (companyId: number): Promise<RemapUnmappedResponse> => {
    return await post<RemapUnmappedResponse>(`/api/alerts/verifi-notices/remap-unmapped`, { company_id: companyId }, { slowApi: true });
  };

  const handleRematchCompany = async (companyId: number): Promise<RemapUnmappedResponse> => {
    return await post<RemapUnmappedResponse>(`/api/alerts/verifi-notices/remap-unmapped`, { company_id: companyId, mode: 'rematch' }, { slowApi: true });
  };

  return (
    <MerchantMappingSettings
      providerLabel="Verifi Notice"
      merchantFieldLabel="Verifi Notice Merchant Name"
      companyId={companyId}
      configs={sharedConfigs}
      ppAccounts={ppAccounts}
      merchantNameOptions={merchantNameOptions}
      loading={loading}
      apiError={apiError}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onRefresh={fetchConfigs}
      syncFeature={{ onSync: handleSync, onSyncCompany: handleSyncCompany, onRematchCompany: handleRematchCompany }}
      disableDescriptors
    />
  );
}
